import {
  Button,
  Card,
  Container,
  Grid,
  InputAdornment,
  Stack,
  Typography,
  CircularProgress,
  Tooltip,
  Box,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import { BiCloudUpload } from 'react-icons/bi';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import SendIcon from '@mui/icons-material/Send';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CloseIcon from '@mui/icons-material/Close';
import BrudCrumbs from '../../components/BreadCrumbs';
import Page from '../../components/Page';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import useGlobalSelector from '../../store/Slice/Global/GlobalSelector';
import {
  getBankDropdownData,
  getCityData,
  getLoanTypeData,
  saveApiData,
  getStateData,
  setCityData,
  savefileApiData
} from '../../store/Slice/Global/GlobalSlice';
import { PayoutApi } from '../../utils/Apis';
import { setApiError, setApiSuccess } from '../../store/Slice/Error/ErrorSlice';
import CustomInput from '../../components/CustomInput';
import CustomSelect from '../../components/CustomSelect';
import { formatDateToDDMMYYYY } from '../../utils/CommonFunction';

const PRIMARY_COLOR = '#0284c7';

const formStyles = {
  '& .formLabel': {
    fontSize: '13px',
    fontWeight: 600,
    lineHeight: 1.5,
    color: '#475569',
    mb: 0.75
  },
  '& .formLabel span': { color: '#ef4444 !important' },
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    fontSize: '13.5px',
    bgcolor: '#ffffff',
    '& fieldset': { borderColor: '#e2e8f0' },
    '&:hover fieldset': { borderColor: '#cbd5e1' },
    '&.Mui-focused fieldset': { borderColor: PRIMARY_COLOR }
  },
  '& .MuiInputBase-inputAdornedStart': { pl: 0 },
  '& .MuiAutocomplete-root .MuiOutlinedInput-root.MuiInputBase-sizeSmall': {
    pl: '14px',
    '& .MuiAutocomplete-input': { pl: 0 }
  }
};

const fieldIcon = (Icon) => (
  <InputAdornment position="start">
    <Icon sx={{ color: '#94a3b8', fontSize: 20 }} />
  </InputAdornment>
);

const rupeeIcon = (
  <InputAdornment position="start">
    <span style={{ color: '#94a3b8', fontSize: '16px', fontWeight: 600 }}>₹</span>
  </InputAdornment>
);

function AddEditPayout() {
  const { rowId } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [sanctionedFile, setSanctionedFile] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [fileUploaded, setFileUploaded] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const {
    handleSubmit: handleReduxSubmit,
    control,
    setValue,
    formState: { errors }
  } = useForm({
    mode: 'onBlur',
    defaultValues: {
      fileNumber: '',
      fileSubmissionDate: '',
      bankId: '',
      stateId: '',
      cityId: '',
      borrowerName: '',
      loanTypeId: '',
      disburseDate: '',
      disburseAmount: '',
      sanctionedDate: '',
      sanctionedAmount: '',
      encashmentDate: '',
      encashmentAmount: ''
    }
  });

  const dispatch = useDispatch();
  const { LoginData } = useLoginSelector();
  const { LoanTypeDrop, BankDrop, CityDrop, stateDrop } = useGlobalSelector();

  const handleSanctionedLetterUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSanctionedFile(file);
    if (file.type?.startsWith('image/') || file.name?.match(/\.(jpg|jpeg|png|gif|webp)$/i)) {
      setPreviewUrl(URL.createObjectURL(file));
    } else {
      setPreviewUrl(null);
    }
    if (!rowId) {
      setFileUploaded(true);
      return;
    }
    setUploadingFile(true);
    setFileUploaded(false);
    try {
      const formData = new FormData();
      formData.append('partnerPayoutId', rowId);
      formData.append('companyId', LoginData?.companyId || '');
      formData.append('userId', LoginData?.userId || '');
      formData.append('file', file);
      const res = await dispatch(savefileApiData(PayoutApi?.uploadSanctionedLetter, formData));
      if (res) {
        dispatch(setApiSuccess('Sanctioned letter uploaded successfully'));
        setFileUploaded(true);
      }
    } catch (error) {
      console.error('Sanctioned letter upload error:', error);
      dispatch(setApiError('Something went wrong'));
    } finally {
      setUploadingFile(false);
    }
  };

  const handleRemoveFile = (e) => {
    e.stopPropagation();
    setSanctionedFile(null);
    setPreviewUrl(null);
    setFileUploaded(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const getLoanTypeBankDropAPi = useCallback(() => {
    if (!LoginData) return;
    const data = {
      companyId: LoginData?.companyId,
      userId: LoginData?.userId
    };
    dispatch(getBankDropdownData(data));
    dispatch(getLoanTypeData(data));
  }, [LoginData, dispatch]);

  const onSubmit = useCallback(
    async (data) => {
      const finalData = {
        ...data,
        disburseDate: formatDateToDDMMYYYY(data.disburseDate),
        sanctionedDate: formatDateToDDMMYYYY(data.sanctionedDate),
        encashmentDate: formatDateToDDMMYYYY(data.encashmentDate),
        companyId: LoginData?.companyId,
        userId: LoginData?.userId,
        employeeClass: 'Others',
        partnerId: null,
        applicationId: null,
        sourceId: 314
      };
      try {
        const res = await dispatch(saveApiData(PayoutApi?.addupdatePayout, finalData));
        if (res?.status === 'success') {
          dispatch(setApiSuccess('Payout saved successfully'));
          const newPartnerPayoutId = res?.data?.partnerPayoutId || res?.data?.id;
          if (newPartnerPayoutId && sanctionedFile && !rowId) {
            const formData = new FormData();
            formData.append('partnerPayoutId', newPartnerPayoutId);
            formData.append('companyId', LoginData?.companyId || '');
            formData.append('userId', LoginData?.userId || '');
            formData.append('file', sanctionedFile);
            await dispatch(savefileApiData(PayoutApi?.uploadSanctionedLetter, formData));
          }
          navigate('/payout');
        }
      } catch (error) {
        console.error('Error submitting form:', error);
        dispatch(setApiError('Something went wrong'));
      }
    },
    [LoginData, sanctionedFile, rowId, dispatch, navigate]
  );

  const handleStateChange = (stateVal) => {
    dispatch(setCityData([]));
    setValue('cityId', null);
    if (stateVal) dispatch(getCityData({ stateId: stateVal }));
  };

  useEffect(() => {
    getLoanTypeBankDropAPi();
  }, [getLoanTypeBankDropAPi]);

  useEffect(() => {
    dispatch(getStateData({ countryId: 101 }));
  }, [dispatch]);

  return (
    <Page title="Partner Management System || Payout">
      <Container maxWidth="xl" sx={{ pb: 6, pt: 2 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          sx={{ mb: 3.5, gap: 2 }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              sx={{
                width: 46,
                height: 46,
                borderRadius: '12px',
                bgcolor: '#e0f2fe',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: PRIMARY_COLOR
              }}
            >
              <PaymentsOutlinedIcon sx={{ fontSize: 26 }} />
            </Box>
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: '#0f172a',
                  fontSize: { xs: '20px', sm: '24px' },
                  letterSpacing: '-0.3px',
                  mb: 0.25
                }}
              >
                {rowId ? 'Edit File' : 'Add New File'}
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b', fontSize: '13px' }}>
                Enter file and loan details for payout processing.
              </Typography>
            </Box>
          </Stack>
          <Box>
            <BrudCrumbs />
          </Box>
        </Stack>

        <Card
          sx={{
            borderRadius: '16px',
            border: '1px solid #e2e8f0',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.04)',
            p: { xs: 2.5, md: 4 },
            bgcolor: '#ffffff',
            position: 'relative'
          }}
        >
          <Box sx={{ mb: 3.5 }}>
            <Box
              sx={{
                display: 'inline-block',
                bgcolor: PRIMARY_COLOR,
                color: '#ffffff',
                px: 2.5,
                py: 0.8,
                borderRadius: '10px',
                fontSize: '13px',
                fontWeight: 600,
                letterSpacing: '0.2px',
                boxShadow: '0 2px 8px rgba(2, 132, 199, 0.25)'
              }}
            >
              File & Loan Details
            </Box>
          </Box>

          <form id="addPayout" onSubmit={handleReduxSubmit(onSubmit)}>
            <Grid container spacing={3} sx={formStyles}>
              <Grid item xs={12} sm={6} md={4}>
                <CustomInput
                  name="fileNumber"
                  label="File No"
                  control={control}
                  errors={errors}
                  isRequired
                  fieldCode="FILE_NO"
                  fieldType="TEXT_FIELD"
                  startAdornment={fieldIcon(DescriptionOutlinedIcon)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <CustomInput
                  name="fileSubmissionDate"
                  label="Submission Date"
                  type="date"
                  control={control}
                  errors={errors}
                  isRequired
                  fieldCode="DOB"
                  fieldType="DATE_FIELD"
                  startAdornment={fieldIcon(EventOutlinedIcon)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <CustomSelect
                  name="bankId"
                  label="Bank Name"
                  control={control}
                  errors={errors}
                  options={BankDrop || []}
                  isRequired
                  placeholder="Select Bank"
                  startAdornment={fieldIcon(AccountBalanceOutlinedIcon)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <CustomSelect
                  name="stateId"
                  label="State"
                  control={control}
                  errors={errors}
                  options={stateDrop || []}
                  isRequired
                  onChange={(e, val) => handleStateChange(val)}
                  placeholder="Select State"
                  startAdornment={fieldIcon(MapOutlinedIcon)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <CustomSelect
                  name="cityId"
                  label="City"
                  control={control}
                  errors={errors}
                  options={CityDrop || []}
                  isRequired
                  placeholder="Select City"
                  startAdornment={fieldIcon(ApartmentOutlinedIcon)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <CustomInput
                  name="borrowerName"
                  label="Borrower Name"
                  control={control}
                  errors={errors}
                  isRequired
                  fieldCode="NAME"
                  fieldType="ALPHABET"
                  startAdornment={fieldIcon(PersonOutlineOutlinedIcon)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <CustomSelect
                  name="loanTypeId"
                  label="Loan Type"
                  control={control}
                  errors={errors}
                  options={LoanTypeDrop || []}
                  isRequired
                  placeholder="Select Loan Type"
                  startAdornment={fieldIcon(CategoryOutlinedIcon)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <CustomInput
                  name="disburseDate"
                  label="Disbursed Date"
                  type="date"
                  control={control}
                  errors={errors}
                  isRequired
                  fieldCode="DATE"
                  fieldType="DATE_FIELD"
                  startAdornment={fieldIcon(EventOutlinedIcon)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <CustomInput
                  name="disburseAmount"
                  label="Disbursed Amount"
                  type="Number"
                  control={control}
                  errors={errors}
                  isRequired
                  fieldType="AMOUNT_FIELD"
                  startAdornment={rupeeIcon}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <CustomInput
                  name="sanctionedDate"
                  label="Sanctioned Date"
                  type="date"
                  control={control}
                  errors={errors}
                  isRequired
                  fieldCode="DATE"
                  fieldType="DATE_FIELD"
                  startAdornment={fieldIcon(EventOutlinedIcon)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <CustomInput
                  name="sanctionedAmount"
                  label="Sanctioned Amount"
                  type="Number"
                  control={control}
                  errors={errors}
                  isRequired
                  fieldType="AMOUNT_FIELD"
                  startAdornment={rupeeIcon}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <CustomInput
                  name="encashmentDate"
                  label="Encashment Date"
                  type="date"
                  control={control}
                  errors={errors}
                  isRequired
                  fieldCode="DATE"
                  fieldType="DATE_FIELD"
                  startAdornment={fieldIcon(EventOutlinedIcon)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <CustomInput
                  name="encashmentAmount"
                  label="Encashment Amount"
                  type="Number"
                  control={control}
                  errors={errors}
                  isRequired
                  fieldType="AMOUNT_FIELD"
                  startAdornment={rupeeIcon}
                />
              </Grid>
              {/* Sanctioned Letter Doc Upload */}
              <Grid item xs={12} sm={6} md={4}>
                <Box>
                  <Typography variant="h6" className="formLabel">
                    Sanctioned Letter Document
                  </Typography>
                  <Tooltip
                    title={
                      fileUploaded
                        ? `Uploaded: ${sanctionedFile?.name || 'File Selected'}`
                        : 'Click to upload PDF / JPG / PNG'
                    }
                    placement="top"
                    arrow
                  >
                    <Box
                      onClick={() => fileInputRef.current?.click()}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        cursor: 'pointer',
                        border: fileUploaded ? '1px solid #16a34a' : '1px solid #e2e8f0',
                        borderRadius: '10px',
                        px: '14px',
                        bgcolor: fileUploaded ? '#f0fdf4' : '#fff',
                        minHeight: '36.4px',
                        transition: 'all 0.2s',
                        '&:hover': {
                          borderColor: fileUploaded ? '#16a34a' : '#cbd5e1'
                        }
                      }}
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        style={{ display: 'none' }}
                        onChange={handleSanctionedLetterUpload}
                      />
                      {uploadingFile ? (
                        <CircularProgress size={16} sx={{ color: PRIMARY_COLOR }} />
                      ) : fileUploaded ? (
                        <CheckCircleIcon sx={{ color: '#16a34a', fontSize: 20 }} />
                      ) : (
                        <BiCloudUpload size={20} style={{ color: '#94a3b8' }} />
                      )}
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: '13.5px',
                          color: fileUploaded ? '#16a34a' : '#94a3b8',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          flex: 1
                        }}
                      >
                        {uploadingFile
                          ? 'Uploading...'
                          : fileUploaded
                          ? sanctionedFile?.name || 'File Selected'
                          : 'Upload document'}
                      </Typography>
                    </Box>
                  </Tooltip>

                  {/* Document Thumbnail Preview Card */}
                  {sanctionedFile && (
                    <Box
                      sx={{
                        mt: 1.5,
                        p: 1.25,
                        borderRadius: '10px',
                        border: '1px solid #cbd5e1',
                        bgcolor: '#f8fafc',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5
                      }}
                    >
                      {/* Thumbnail representation */}
                      {previewUrl ? (
                        <Box
                          component="img"
                          src={previewUrl}
                          alt="Sanctioned Letter Thumbnail"
                          onClick={() => setIsPreviewOpen(true)}
                          sx={{
                            width: 52,
                            height: 52,
                            borderRadius: '8px',
                            objectFit: 'cover',
                            border: '1px solid #e2e8f0',
                            cursor: 'pointer',
                            '&:hover': { opacity: 0.85 }
                          }}
                        />
                      ) : (
                        <Box
                          onClick={() => setIsPreviewOpen(true)}
                          sx={{
                            width: 52,
                            height: 52,
                            borderRadius: '8px',
                            bgcolor: '#fee2e2',
                            color: '#dc2626',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            fontSize: '11px',
                            fontWeight: 700
                          }}
                        >
                          <PictureAsPdfIcon sx={{ fontSize: 24, mb: 0.2 }} />
                          PDF
                        </Box>
                      )}

                      {/* File info */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontSize: '13px',
                            fontWeight: 600,
                            color: '#0f172a',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {sanctionedFile.name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#64748b', fontSize: '11.5px' }}>
                          {(sanctionedFile.size / 1024).toFixed(1)} KB
                        </Typography>
                      </Box>

                      {/* Action icons */}
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title="View Preview" arrow>
                          <IconButton
                            size="small"
                            onClick={() => setIsPreviewOpen(true)}
                            sx={{
                              color: '#0284c7',
                              bgcolor: '#e0f2fe',
                              '&:hover': { bgcolor: '#bae6fd' }
                            }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Remove File" arrow>
                          <IconButton
                            size="small"
                            onClick={handleRemoveFile}
                            sx={{
                              color: '#dc2626',
                              bgcolor: '#fee2e2',
                              '&:hover': { bgcolor: '#fca5a5' }
                            }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Box>
                  )}
                </Box>
              </Grid>
            </Grid>

            <Stack direction="row" justifyContent="flex-end" spacing={2} sx={{ mt: 4 }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/payout')}
                sx={{
                  borderRadius: '10px',
                  px: 3.5,
                  py: '7.8px',
                  fontWeight: 600,
                  fontSize: '14px',
                  textTransform: 'none',
                  borderColor: '#e2e8f0',
                  color: '#475569',
                  bgcolor: '#ffffff',
                  '&:hover': {
                    borderColor: '#cbd5e1',
                    bgcolor: '#f8fafc'
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                endIcon={<SendIcon sx={{ fontSize: 16 }} />}
                sx={{
                  bgcolor: PRIMARY_COLOR,
                  color: '#ffffff',
                  borderRadius: '10px',
                  px: 4,
                  py: 1.1,
                  fontWeight: 600,
                  fontSize: '14px',
                  textTransform: 'none',
                  boxShadow: '0 4px 14px rgba(2, 132, 199, 0.25)',
                  '&:hover': {
                    bgcolor: '#0369a1'
                  }
                }}
              >
                Submit File
              </Button>
            </Stack>
          </form>
        </Card>

        {/* Modal Dialog for Full Document Preview */}
        <Dialog
          open={isPreviewOpen}
          onClose={() => setIsPreviewOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden' } }}
        >
          <DialogTitle
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              fontWeight: 700,
              fontSize: '16px',
              color: '#0f172a',
              pb: 1.5
            }}
          >
            <span>Sanctioned Letter Document Preview</span>
            <IconButton size="small" onClick={() => setIsPreviewOpen(false)}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent
            dividers
            sx={{ display: 'flex', justifyContent: 'center', p: 3, bgcolor: '#f8fafc' }}
          >
            {previewUrl ? (
              <Box
                component="img"
                src={previewUrl}
                alt="Sanctioned Letter Full View"
                sx={{
                  maxWidth: '100%',
                  maxHeight: '70vh',
                  objectFit: 'contain',
                  borderRadius: '8px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                }}
              />
            ) : (
              <Box sx={{ width: '100%', height: '65vh' }}>
                <iframe
                  src={sanctionedFile ? URL.createObjectURL(sanctionedFile) : ''}
                  title="Sanctioned Letter Document"
                  width="100%"
                  height="100%"
                  style={{ border: 'none', borderRadius: '8px' }}
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button
              variant="contained"
              onClick={() => setIsPreviewOpen(false)}
              sx={{
                bgcolor: '#0284c7',
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 600,
                px: 3
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Page>
  );
}
export default AddEditPayout;
